import { WinePackageInfo } from './WinePackageInfo';
import useMouse from './hooks/useMouse';
import { WinePackageWidgetModel } from '../../models/WinePackageWidgetModel';
import { CartComponent } from './cart/CartComponent';
import { DiscountTag } from './components/DiscountTag';
import { WinePackageImage } from './components/WinePackageImage';
import { useCalculatedDiscount } from 'shared-widget-components/src/hooks/useCalculatedDiscount';
import { useWinePackageClasses } from './hooks/useWinePackageClasses';

interface PackageProps {
  winePackage: WinePackageWidgetModel;

  // false in drag and drop, true in preview
  isShowCart: boolean;
  isShowContent: boolean;
}

/**
 * The WinePackage component renders a wine package with its image, information, and cart component.
 * @param winePackage - The object containing information about the wine package.
 * @param isShowCart - The boolean value to show the cart component.
 * @param isShowContent - The boolean value to show the content of the wine package.
 * @returns The WinePackage component is returning JSX elements.
 */

export const WinePackage = ({ winePackage, isShowCart, isShowContent }: PackageProps) => {
  const { isCartVisible, handleMouseEnter, handleMouseLeave } = useMouse(isShowCart);

  const basePrice = winePackage.basePrice!;
  const currentPrice = winePackage.currentPrice!;

  const { calculatedDiscount } = useCalculatedDiscount({ basePrice, currentPrice });

  const { classnameWithCart, classnameWithoutCart, slideUpDivClasses } = useWinePackageClasses(winePackage.showEurPrice);

  return (
    <div
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={`min-[350px]:w-[330px] lg:w-[370px] ${winePackage.showEurPrice ? 'min-[350px]:h-[465px] lg:h-[390px]' : 'min-[350px]:h-[465px] lg:h-[370px]'} rounded-md relative overflow-hidden bg-[#F6F5F2]`}
    >
      <WinePackageImage winePackage={winePackage} />
      <DiscountTag discount={calculatedDiscount} />
      <div
        className={`min-[350px]:translate-y-0 ${isCartVisible ? `${classnameWithCart}` : `${classnameWithoutCart}`} 
           rounded-lg min-[350px]:p-[8px] min-[350px]:h-1/2 lg:h-3/5 items-center ${slideUpDivClasses}`}
      >
        <WinePackageInfo winePackage={winePackage} isShowContent={isShowContent} />
        {isShowCart && <CartComponent winePackage={winePackage} />}
      </div>
    </div>
  );
};
